import React, { useState } from 'react';
import DemoBanner from '../../components/DemoBanner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiCheck, FiX, FiArrowRight, FiInfo, FiSearch, FiFileText } = FiIcons;

const VacatureMatch = () => {
  const [cvText, setCvText] = useState('');
  const [jobText, setJobText] = useState('');
  const [isMatching, setIsMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  const POTENTIAL_TERMS = [
    'sql', 'scrum', 'agile', 'python', 'projectmanagement', 'stakeholdermanagement', 
    'hbo', 'wo', 'master', 'bachelor', 'communicatief', 'analytisch', 'it', 
    'software', 'engineering', 'rijbewijs b', 'prince2', 'lean', 'six sigma'
  ];

  const handleMatch = () => {
    if (!cvText.trim() || !jobText.trim()) return;
    setIsMatching(true);

    setTimeout(() => {
      const lowerCv = cvText.toLowerCase();
      const lowerJob = jobText.toLowerCase();

      // 🧩 STAP 3: MATCHLOGICA (ALLEEN LETTERLIJKE OVERLAP)
      const jobTerms = POTENTIAL_TERMS.filter(term => lowerJob.includes(term));
      const cvTerms = POTENTIAL_TERMS.filter(term => lowerCv.includes(term));

      const overlapping = jobTerms.filter(term => cvTerms.includes(term));
      const missing = jobTerms.filter(term => !cvTerms.includes(term));

      setMatchResult({
        overlapping,
        missing
      });
      setIsMatching(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen py-16 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <DemoBanner />
        
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiTarget} className="text-blue-600 text-2xl" />
          </div>
          <h1 className="text-4xl font-bold text-brand-dark mb-4 tracking-tight">Vacature-match</h1>
          <p className="text-xl text-gray-600">
            Letterlijke vergelijking tussen CV en Vacature. Geen aannames.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <label className="block font-bold mb-4 flex items-center gap-2">
              <SafeIcon icon={FiFileText} className="text-blue-600" />
              Jouw CV (Tekst)
            </label>
            <textarea
              value={cvText}
              onChange={(e) => setCvText(e.target.value)}
              className="w-full h-64 p-4 border border-gray-100 rounded-xl resize-none outline-none focus:ring-2 focus:ring-brand-orange text-sm"
              placeholder="Plak hier de tekst van je CV..."
            />
          </div>
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <label className="block font-bold mb-4 flex items-center gap-2">
              <SafeIcon icon={FiSearch} className="text-brand-orange" />
              De Vacaturetekst
            </label>
            <textarea
              value={jobText}
              onChange={(e) => setJobText(e.target.value)}
              className="w-full h-64 p-4 border border-gray-100 rounded-xl resize-none outline-none focus:ring-2 focus:ring-brand-orange text-sm"
              placeholder="Plak hier de vacaturetekst..."
            />
          </div>
        </div>

        <button
          onClick={handleMatch}
          disabled={isMatching || !cvText || !jobText}
          className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 mb-12 ${
            isMatching || !cvText || !jobText ? 'bg-gray-300 cursor-not-allowed' : 'bg-brand-dark hover:bg-gray-900'
          }`}
        >
          {isMatching ? 'Match berekenen op basis van tekstoverlap...' : 'Bereken Match'}
          {!isMatching && <SafeIcon icon={FiArrowRight} />}
        </button>

        {matchResult && (
          <div className="space-y-8 animate-fade-in pb-12">
            <div className="grid md:grid-cols-2 gap-8">
              {/* ✅ STAP 2: GESCHEIDEN LIJSTEN */}
              <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-bold text-green-700 mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiCheck} />
                  ✅ Gevonden in CV & Vacature
                </h3>
                <div className="flex flex-wrap gap-2">
                  {matchResult.overlapping.length > 0 ? (
                    matchResult.overlapping.map((term, i) => (
                      <span key={i} className="px-3 py-1 bg-green-50 text-green-700 rounded-md text-xs font-bold uppercase border border-green-100">
                        {term}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 italic text-sm">Geen letterlijke overlap gevonden.</span>
                  )}
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-bold text-red-700 mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiX} />
                  ❌ Ontbreekt in CV (wel in vacature)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {matchResult.missing.length > 0 ? (
                    matchResult.missing.map((term, i) => (
                      <span key={i} className="px-3 py-1 bg-red-50 text-red-700 rounded-md text-xs font-bold uppercase border border-red-100">
                        {term}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 italic text-sm">Geen ontbrekende termen gedetecteerd.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-100 rounded-xl flex items-start gap-3 border border-gray-200">
              <SafeIcon icon={FiInfo} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-gray-500 leading-relaxed uppercase tracking-wide font-bold">
                🧾 VERPLICHTE DISCLAIMER: Deze analyse is gebaseerd uitsluitend op expliciet genoemde eisen in de aangeleverde vacaturetekst. Er zijn geen aannames of aanvullingen gedaan.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VacatureMatch;